/*
 * RCHIVE behaviours layered on top of Dawn.
 * - Uses Dawn globals (routes, subscribe/publish, PUB_SUB_EVENTS) only when present.
 * - Every element is a guarded custom element, so section re-renders in the
 *   theme editor re-initialise cleanly and nothing runs twice.
 */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const inDesignMode = () => Boolean(window.Shopify && window.Shopify.designMode);

  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };

  /* ---------- Ticker: pause / play control (WCAG 2.2.2) ---------- */

  define(
    'rch-ticker',
    class RchTicker extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('.rch-ticker__toggle');
        if (!this.toggle) return;
        this.onToggle = this.onToggle.bind(this);
        this.toggle.addEventListener('click', this.onToggle);
      }

      disconnectedCallback() {
        this.toggle?.removeEventListener('click', this.onToggle);
      }

      onToggle() {
        const paused = !this.hasAttribute('data-paused');
        this.toggleAttribute('data-paused', paused);
        this.toggle.setAttribute(
          'aria-label',
          paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause
        );
      }
    }
  );

  /* ---------- Hero: fade / scale while the page scrolls over it ---------- */

  define(
    'rch-hero',
    class RchHero extends HTMLElement {
      connectedCallback() {
        if (!this.hasAttribute('data-scroll-fade')) return;
        this.ticking = false;
        this.onScroll = this.onScroll.bind(this);
        this.update = this.update.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        reducedMotion.addEventListener?.('change', this.onScroll);
        this.update();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        reducedMotion.removeEventListener?.('change', this.onScroll);
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(this.update);
      }

      update() {
        this.ticking = false;
        if (reducedMotion.matches || inDesignMode() || window.innerWidth < 750) {
          this.style.removeProperty('--rch-progress');
          return;
        }
        const progress = Math.min(1, Math.max(0, window.scrollY / Math.max(1, window.innerHeight)));
        this.style.setProperty('--rch-progress', progress.toFixed(3));
      }
    }
  );

  /* ---------- Product card size quick add ---------- */

  define(
    'rch-size-add',
    class RchSizeAdd extends HTMLElement {
      connectedCallback() {
        this.idInput = this.querySelector('input[name="id"]');
        this.submitButton = this.querySelector('[type="submit"]');
        this.onChange = this.onChange.bind(this);
        this.addEventListener('change', this.onChange);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
      }

      onChange(event) {
        const radio = event.target.closest('input[type="radio"]');
        if (!radio || !this.idInput) return;
        this.idInput.value = radio.value;
        const available = radio.dataset.available === 'true';
        this.submitButton?.toggleAttribute('disabled', !available);
        this.submitButton?.removeAttribute('aria-disabled');
      }
    }
  );

  /* ---------- Add-to-cart price label follows the selected variant ---------- */

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const data = event && event.data;
      if (!data || !data.html || !data.sectionId) return;
      const buttonId = `ProductSubmitButton-${data.sectionId}`;
      const nextPrice = data.html.getElementById(buttonId)?.querySelector('.rch-atc-price');
      const currentPrice = document.getElementById(buttonId)?.querySelector('.rch-atc-price');
      if (nextPrice && currentPrice) currentPrice.textContent = nextPrice.textContent;
    });
  }

  /* ---------- Cart discount code ---------- */

  define(
    'rch-cart-discount',
    class RchCartDiscount extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.input = this.querySelector('input[name="discount"]');
        this.status = this.querySelector('.rch-discount__status');
        this.onSubmit = this.onSubmit.bind(this);
        this.onClick = this.onClick.bind(this);
        this.form?.addEventListener('submit', this.onSubmit);
        this.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.onSubmit);
        this.removeEventListener('click', this.onClick);
      }

      get appliedCodes() {
        return Array.from(this.querySelectorAll('[data-code]')).map((element) => element.dataset.code);
      }

      onSubmit(event) {
        if (!window.routes || !window.fetch) return; // fall back to ?discount= at checkout
        event.preventDefault();
        const code = this.input.value.trim();
        if (!code) {
          this.setStatus(this.status.dataset.empty, 'error');
          this.input.focus();
          return;
        }
        this.apply([...this.appliedCodes, code], code);
      }

      onClick(event) {
        const removeButton = event.target.closest('[data-remove-code]');
        if (!removeButton) return;
        const code = removeButton.dataset.removeCode;
        this.apply(this.appliedCodes.filter((applied) => applied !== code));
      }

      async apply(codes, newCode = null) {
        const isDrawer = this.dataset.context === 'drawer';
        this.setAttribute('aria-busy', 'true');
        this.querySelectorAll('button').forEach((button) => button.setAttribute('disabled', ''));

        try {
          let cart = await this.update(codes, isDrawer);

          if (newCode) {
            const match = (cart.discount_codes || []).find(
              (discount) => discount.code.toLowerCase() === newCode.toLowerCase()
            );
            if (!match || !match.applicable) {
              // Keep the cart clean: drop the code that did not apply.
              cart = await this.update(codes.slice(0, -1), isDrawer);
              this.render(cart, isDrawer, 'error');
              return;
            }
          }

          this.render(cart, isDrawer, newCode ? 'success' : null);
        } catch (error) {
          console.error(error);
          this.setStatus(this.status?.dataset.error, 'error');
          this.removeAttribute('aria-busy');
          this.querySelectorAll('button').forEach((button) => button.removeAttribute('disabled'));
        }
      }

      async update(codes, isDrawer) {
        const body = { discount: codes.join(',') };
        if (isDrawer) {
          body.sections = ['cart-drawer', 'cart-icon-bubble'];
          body.sections_url = window.location.pathname;
        }
        const response = await fetch(`${window.routes.cart_update_url}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        if (!response.ok) throw new Error(`Discount update failed: ${response.status}`);
        return response.json();
      }

      render(cart, isDrawer, outcome) {
        const drawer = document.querySelector('cart-drawer');
        if (isDrawer && drawer && cart.sections) {
          drawer.renderContents(cart);
          const fresh = document.querySelector('rch-cart-discount[data-context="drawer"]');
          if (fresh && outcome) {
            fresh.setStatus(outcome === 'error' ? fresh.status.dataset.error : fresh.status.dataset.success, outcome);
          }
          return;
        }
        if (outcome === 'error') {
          this.setStatus(this.status?.dataset.error, 'error');
          this.removeAttribute('aria-busy');
          this.querySelectorAll('button').forEach((button) => button.removeAttribute('disabled'));
          this.input?.focus();
          return;
        }
        // Cart page: reload so Dawn re-renders items, totals and discounts together.
        window.location.reload();

      }

      setStatus(message, type) {
        if (!this.status || !message) return;
        this.status.textContent = message;
        this.status.classList.toggle('rch-discount__status--error', type === 'error');
        this.status.classList.toggle('rch-discount__status--success', type === 'success');
      }
    }
  );
})();
